import { yupResolver } from '@hookform/resolvers/yup';
import { LoadingButton } from '@mui/lab';
import {
  Stack,
  Dialog,
  Button,
  MenuItem,
  Typography,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import { useForm } from 'react-hook-form';
import FormProvider, { RHFSelect, RHFTextField, RHFUploadBox } from 'src/components/hook-form';
import { PositionTxType, UserTransactionPaymentType } from 'src/type/position';
import { TransactionType } from 'src/type/transaction';
import axios, { endpoints } from 'src/utils/axios';
import { toastError } from 'src/utils/format-error';
import * as Yup from 'yup';
import { useCallback } from 'react';
import { fData } from 'src/utils/format-number';
import { uploadImage } from 'src/libs/firebase/firebase-upload';
import InfoGrid from 'src/components/ui-kit/info-grid';
import { BranchType } from 'src/type/branch';
// import customParseFormat from 'dayjs/plugin/customParseFormat';
// import utc from 'dayjs/plugin/utc';
// import timezone from 'dayjs/plugin/timezone';
import ImageWithPreview from 'src/components/ui-kit/image-with-preview';
import useCurrentDateTime from 'src/hooks/use-current-date-time';
import { txAction, paymentTypeLabel } from './utils';

// dayjs.extend(customParseFormat);
// dayjs.extend(utc);
// dayjs.extend(timezone);

type ApproveDialogProps = {
  open: boolean;
  onClose: () => void;
  tx: PositionTxType;
  handleReload: () => void;
  branches: BranchType[];
};

// const paymentTypeOptions = [
//   {
//     label: 'โอนเงิน',
//     value: 'BANK',
//   },
//   {
//     label: 'เงินสด',
//     value: 'CASH',
//   },
//   {
//     label: 'เครดิต',
//     value: 'CREDIT',
//   },
// ];

export default function BuyPositionApproveDialog({
  open,
  onClose,
  tx,
  handleReload,
  branches,
}: ApproveDialogProps) {
  const currentDateTime = useCurrentDateTime();
  const getInfo = () => {
    if (!tx) return [];

    const { paymentImageUrl, paymentType } = tx;

    return [
      {
        label: 'วิธีการชําระเงิน',
        value: paymentTypeLabel(paymentType),
      },
      ...(paymentImageUrl
        ? [
            {
              label: 'หลักฐานการโอนเงิน',
              value: <ImageWithPreview src={paymentImageUrl} alt="" />,
            },
          ]
        : []),
    ];
  };

  const Schema = Yup.object().shape({
    // paymentImage: Yup.mixed<File>().when([], {
    //   is: () => !tx?.paymentImageUrl && tx?.paymentType !== UserTransactionPaymentType.CREDIT,
    //   then: (schema) => schema.required('กรุณาแนบหลักฐานการชำระเงิน'),
    //   otherwise: (schema) => schema.notRequired(),
    // }),
    paymentImage: Yup.mixed<File>().notRequired(),
    branchId: Yup.string().required('กรุณาเลือกสาขา'),
    note: Yup.string(),
  });

  const defaultValues = {
    paymentImage: null as unknown as File,
    branchId: '',
    note: '',
  };

  const methods = useForm({
    mode: 'onChange',
    resolver: yupResolver(Schema),
    defaultValues,
  });

  const {
    handleSubmit,
    reset,
    setValue,
    formState: { isSubmitting },
  } = methods;

  const beforeClose = () => {
    reset(defaultValues);
    onClose();
  };

  const onSubmit = handleSubmit(async (values) => {
    try {
      const body = {
        branchId: values.branchId,
        note: values.note,
        currentDateTime,
      };

      if (values.paymentImage) {
        const paymentImageUrl = await uploadImage(values.paymentImage, 'images');
        Object.assign(body, { paymentImageUrl });
      }

      await axios.post(endpoints.transactions.position.buyApprove(tx.id), body);

      handleReload();
      beforeClose();
      enqueueSnackbar('อัพเดทข้อมูลสำเร็จ', { variant: 'success' });
    } catch (error) {
      toastError(error);
    }
  });

  const handleDropImage = useCallback(
    (acceptedFiles: File[]) => {
      const file = acceptedFiles[0];

      const newFile = Object.assign(file, {
        preview: URL.createObjectURL(file),
      });

      if (file) {
        setValue('paymentImage', newFile, { shouldValidate: true });
      }
    },
    [setValue]
  );

  const branchOption =
    branches && branches.length > 0
      ? branches.map((branch) => ({
          label: branch.name,
          value: branch.id,
        }))
      : [];

  return (
    <Dialog fullWidth maxWidth="sm" open={open} onClose={onClose}>
      <FormProvider methods={methods} onSubmit={onSubmit}>
        <Stack direction="row" alignItems="center" sx={{ p: 3 }} flexWrap="wrap">
          <Typography variant="h6">อนุมัติการ</Typography>
          <Typography
            color={tx.type === TransactionType.BUY_POSITION ? 'success.main' : 'error'}
            variant="h6"
            mx={0.5}
          >
            {txAction(tx.type)}
          </Typography>
          <Typography variant="h6">
            {tx.product.name} {tx.amount} {tx.unitName}
          </Typography>
        </Stack>

        <DialogContent>
          <InfoGrid infos={getInfo()} />

          <Stack spacing={3} py={3}>
            {/* <RHFSelect
              name="paymentType"
              label="ช่องทางการชำระเงิน"
              placeholder="เลือกช่องทางการชำระเงิน"
              disabled
            >
              {paymentTypeOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </RHFSelect> */}
            {!tx?.paymentImageUrl && tx?.paymentType !== UserTransactionPaymentType.CREDIT && (
              <RHFUploadBox
                name="paymentImage"
                maxSize={3145728}
                onDrop={handleDropImage}
                placeholder="อัพโหลดรูปหลักฐาน"
                sx={{
                  maxHeight: 140,
                  width: '100%',
                  height: '100%',
                  aspectRatio: '1/1',
                  mx: 'auto',
                }}
                helperText={
                  <>
                    อนุญาตไฟล์ *.jpeg, *.jpg, *.png, *.gif
                    <br /> ขนาดสูงสุด {fData(3145728)}
                  </>
                }
              />
            )}

            <RHFSelect name="branchId" label="สาขาที่ดำเนินการ" placeholder="เลือกสาขาที่ดำเนินการ">
              {branchOption.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </RHFSelect>

            <RHFTextField
              multiline
              rows={3}
              name="note"
              placeholder="หมายเหตุ"
              InputLabelProps={{ shrink: true }}
            />
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button variant="outlined" onClick={beforeClose} disabled={isSubmitting}>
            ยกเลิก
          </Button>
          <LoadingButton variant="contained" type="submit" loading={isSubmitting}>
            ดำเนินการต่อ
          </LoadingButton>
        </DialogActions>
      </FormProvider>
    </Dialog>
  );
}
